import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { parseLvPdf, LvParseError } from '../lib/lvParser';
import { parseLvTabelle } from '../lib/lvTableParser';

// ─── E3.1.3: LV-Import-Page ──────────────────────────────────────────────────
// PDF-LV im Browser parsen (lvParser.js, E3.1.2), Vorschau MIT Pflicht-
// Bestaetigung, dann Bulk-Save (E3.1.1). Die Original-PDF wird weder
// hochgeladen noch gespeichert - nur die bestaetigten Zeilen gehen als JSON
// an /api/projekt-positionen/bulk-import.

function fmtEur(n) {
  if (n == null || !Number.isFinite(Number(n))) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtNum(n) {
  if (n == null || !Number.isFinite(Number(n))) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

// Zeile importierbar? Gleiche Regeln wie die Server-Pre-Pass-Validation
// (bulk-import): Kurztext Pflicht, Menge > 0, EP/GP >= 0.
function zeileValide(r) {
  return (
    typeof r.bezeichnung === 'string' && r.bezeichnung.trim() !== '' &&
    Number.isFinite(Number(r.sollmenge)) && Number(r.sollmenge) > 0 &&
    Number.isFinite(Number(r.einheitspreis)) && Number(r.einheitspreis) >= 0 &&
    Number.isFinite(Number(r.gesamtpreis)) && Number(r.gesamtpreis) >= 0
  );
}

// Warnungen live berechnen (nicht die Parser-Warnungen cachen), damit
// Inline-Edits in der Vorschau sofort korrekt bewertet werden.
function zeileWarnungen(r, alle, bestehendePosNr) {
  const w = [];
  const menge = Number(r.sollmenge);
  const ep = Number(r.einheitspreis);
  const gp = Number(r.gesamtpreis);
  if (!zeileValide(r)) {
    w.push('Unvollständig: Kurztext, Menge, EP und GP werden benötigt');
    return w;
  }
  const rechnerisch = menge * ep;
  if (Math.abs(rechnerisch - gp) > Math.max(1, gp * 0.005)) {
    w.push(`GP weicht von Menge × EP ab (rechnerisch ${fmtEur(rechnerisch)})`);
  }
  if (Math.round(ep * 1000) % 10 !== 0) {
    w.push('EP hat 3 Nachkommastellen – wird auf 2 gerundet gespeichert, GP bleibt wie im LV');
  }
  if (!r.einheit || String(r.einheit).trim() === '') w.push('Einheit fehlt');
  if (String(r.einheit || '').trim().length > 20) w.push('Einheit zu lang (max. 20 Zeichen)');
  if (r.pos_nr && alle.filter((x) => x.pos_nr === r.pos_nr).length > 1) {
    w.push('Positionsnummer mehrfach im Import');
  }
  if (r.pos_nr && bestehendePosNr.has(r.pos_nr)) {
    w.push('Positionsnummer existiert bereits auf der Ziel-Baustelle');
  }
  return w;
}

const inputStyle = {
  background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8,
  padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, outline: 'none', width: '100%',
};
const cellInputStyle = { ...inputStyle, padding: '4px 8px', fontSize: 13 };

export default function LvImport() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);   // Parser-Resultat (inkl. uebersprungen/statistik)
  const [rows, setRows] = useState([]);             // editierbare Positions-Kopien
  const [langtextOffen, setLangtextOffen] = useState(() => new Set());
  const [zeigeUebersprungen, setZeigeUebersprungen] = useState(false);

  const [baustellen, setBaustellen] = useState([]);
  const [zielModus, setZielModus] = useState('neu');
  const [zielBaustelleId, setZielBaustelleId] = useState('');
  const [neuFelder, setNeuFelder] = useState({ name: '', adresse: '', plz: '', ort: '', auftraggeber: '', notizen: '' });
  const [bestehendePosNr, setBestehendePosNr] = useState(() => new Set());

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    api.get('/baustellen').then((r) => setBaustellen(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, navigate]);

  // Dubletten-Check gegen die Ziel-Baustelle: vorhandene pos_nr laden.
  useEffect(() => {
    setBestehendePosNr(new Set());
    if (zielModus !== 'vorhanden' || !zielBaustelleId) return;
    api.get(`/projekt-positionen?baustelle_id=${zielBaustelleId}`)
      .then((r) => setBestehendePosNr(new Set((r.data || []).map((p) => p.pos_nr).filter(Boolean))))
      .catch(() => {});
  }, [zielModus, zielBaustelleId]);

  const dateiGewaehlt = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // gleiche Datei erneut waehlbar
    if (!file) return;
    setParsing(true);
    setParseError(null);
    setErgebnis(null);
    setRows([]);
    setSaveResult(null);
    setSaveError(null);
    setLangtextOffen(new Set());
    try {
      // E3.2: Quelle per Datei-Endung waehlen. Shape ist bei beiden Parsern
      // identisch - der restliche Handler bleibt unveraendert.
      const endung = (file.name.split('.').pop() || '').toLowerCase();
      let r;
      if (endung === 'pdf') {
        r = await parseLvPdf(file);
      } else if (endung === 'xlsx' || endung === 'xls' || endung === 'csv') {
        r = await parseLvTabelle(file);
      } else {
        throw new LvParseError('Nicht unterstütztes Format. Erlaubt sind PDF, Excel (.xlsx/.xls) und CSV.');
      }
      setErgebnis(r);
      setRows(r.positionen.map((p) => ({ ...p, ausgewaehlt: zeileValide(p) })));
      // Kopfdaten als Vorschlag fuer "neue Baustelle" - alles editierbar.
      const notizenTeile = [
        r.kopf.projektnummer ? `LV/Projekt-Nr.: ${r.kopf.projektnummer}` : null,
        r.kopf.lvTitel || null,
        r.kopf.datum ? `LV-Datum: ${r.kopf.datum}` : null,
      ].filter(Boolean);
      setNeuFelder({
        name: r.kopf.name || '',
        adresse: r.kopf.adresse || '',
        plz: r.kopf.plz || '',
        ort: r.kopf.ort || '',
        auftraggeber: r.kopf.auftraggeber || '',
        notizen: notizenTeile.join(' · '),
      });
      setZielModus('neu');
      setZielBaustelleId('');
    } catch (err) {
      setParseError(err instanceof LvParseError
        ? err.message
        : 'Unerwarteter Fehler beim Lesen der Datei.');
    }
    setParsing(false);
  };

  const setRow = useCallback((idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }, []);

  const toggleLangtext = (idx) => {
    setLangtextOffen((prev) => {
      const n = new Set(prev);
      if (n.has(idx)) n.delete(idx); else n.add(idx);
      return n;
    });
  };

  // Aehnlichkeits-Hinweis: erkannter Name vs. vorhandene Baustellen.
  const aehnliche = zielModus === 'neu' && neuFelder.name.trim() !== ''
    ? baustellen.filter((b) => {
        const a = b.name.toLowerCase();
        const n = neuFelder.name.trim().toLowerCase();
        return a.includes(n) || n.includes(a);
      })
    : [];

  const ausgewaehlt = rows.filter((r) => r.ausgewaehlt);
  const ausgewaehltInvalid = ausgewaehlt.filter((r) => !zeileValide(r));
  const zielOk = zielModus === 'vorhanden' ? zielBaustelleId !== '' : neuFelder.name.trim() !== '';
  const importMoeglich = zielOk && ausgewaehlt.length > 0 && ausgewaehltInvalid.length === 0 && !saving;

  const warnungenGesamt = rows.reduce(
    (s, r) => s + (r.ausgewaehlt ? zeileWarnungen(r, rows, bestehendePosNr).length : 0), 0
  );
  const summeGpAusgewaehlt = ausgewaehlt.reduce((s, r) => s + (Number(r.gesamtpreis) || 0), 0);

  const importieren = async () => {
    if (!importMoeglich || !ergebnis) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        baustelle: zielModus === 'vorhanden'
          ? { modus: 'vorhanden', id: parseInt(zielBaustelleId) }
          : { modus: 'neu', daten: { ...neuFelder } },
        quelle_datei: ergebnis.dateiname,
        positionen: ausgewaehlt.map((r) => ({
          pos_nr: r.pos_nr || null,
          bezeichnung: String(r.bezeichnung).trim(),
          // E3.x: LV-Volltext aus dem Parser wird jetzt mitgespeichert.
          langtext: r.langtext || null,
          kategorie: r.kategorie || null,
          einheit: r.einheit || null,
          sollmenge: Number(r.sollmenge),
          einheitspreis: Number(r.einheitspreis),
          gesamtpreis: Number(r.gesamtpreis),
        })),
      };
      const resp = await api.post('/projekt-positionen/bulk-import', payload);
      setSaveResult(resp.data);
    } catch (err) {
      const d = err.response?.data;
      setSaveError(d?.error
        ? (d.zeile != null ? `Zeile ${d.zeile}: ${d.error}` : d.error)
        : 'Import fehlgeschlagen – es wurde nichts gespeichert.');
    }
    setSaving(false);
  };

  // ── Erfolgs-Ansicht ──
  if (saveResult) {
    const ziel = baustellen.find((b) => b.id === saveResult.baustelle_id);
    return (
      <div>
        <div className="page-header">
          <h1>LV-Import</h1>
          <p>PDF-Leistungsverzeichnis als Projektpositionen übernehmen</p>
        </div>
        <div className="card card-accent" style={{ maxWidth: 640 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>
            ✅ Import erfolgreich
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>
            {saveResult.angelegt} Position{saveResult.angelegt === 1 ? '' : 'en'} wurde{saveResult.angelegt === 1 ? '' : 'n'} angelegt
            {saveResult.baustelle_neu
              ? ' – auf einer neu angelegten Baustelle.'
              : ziel ? ` – auf Baustelle „${ziel.name}“.` : '.'}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <Link to="/projektpositionen" className="btn btn-primary">→ Zu den Projektpositionen</Link>
            <button type="button" className="btn" onClick={() => { setSaveResult(null); setErgebnis(null); setRows([]); }}>
              Weiteres LV importieren
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>LV-Import</h1>
        <p>PDF-Leistungsverzeichnis als Projektpositionen übernehmen</p>
      </div>

      {/* Schritt 1: Datei waehlen */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><div className="card-title">1 · LV-Datei wählen (PDF, Excel oder CSV)</div></div>
        <div style={{
          background: 'var(--bg-elevated)', color: 'var(--text-3)', padding: '8px 12px',
          borderRadius: 'var(--radius)', fontSize: 12, marginBottom: 12, lineHeight: 1.5,
        }}>
          Die Datei wird <strong>lokal im Browser</strong> gelesen – es wird nichts hochgeladen
          oder gespeichert. Unterstützt werden LV-PDFs mit Spaltenkopf „Ordnungszahl / Menge /
          Einheitspreis / Gesamtpreis" (AVA-Export) sowie Excel-/CSV-Tabellen mit Kopfzeile
          (Bezeichnung, Menge, EP, GP; optional Pos.-Nr., Einheit, Langtext). Bei unbekanntem
          Layout bricht der Import ab, bevor etwas übernommen wird. Gespeichert wird erst nach
          deiner Bestätigung in der Vorschau.
        </div>
        <input
          type="file"
          accept=".pdf,.xlsx,.xls,.csv"
          onChange={dateiGewaehlt}
          disabled={parsing}
          style={{ color: 'var(--text-2)', fontSize: 14 }}
        />
        {parsing && (
          <div style={{ marginTop: 12, color: 'var(--text-3)', fontSize: 13 }}>
            <span className="spinner" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
            Datei wird gelesen…
          </div>
        )}
        {parseError && (
          <div style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)',
            background: 'rgba(220,80,80,0.12)', border: '1px solid rgba(220,80,80,0.4)',
            color: 'var(--text-1)', fontSize: 13, lineHeight: 1.5,
          }}>
            ⚠️ {parseError}
          </div>
        )}
      </div>

      {ergebnis && (
        <>
          {/* Schritt 2: Ziel-Baustelle */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-head"><div className="card-title">2 · Ziel-Baustelle</div></div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <label style={{ fontSize: 14, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" checked={zielModus === 'neu'} onChange={() => setZielModus('neu')} />
                Neue Baustelle anlegen (aus LV-Kopfdaten)
              </label>
              <label style={{ fontSize: 14, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" checked={zielModus === 'vorhanden'} onChange={() => setZielModus('vorhanden')} />
                Vorhandene Baustelle
              </label>
            </div>

            {zielModus === 'neu' ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                {[
                  ['name', 'Name *'], ['adresse', 'Adresse'], ['plz', 'PLZ'],
                  ['ort', 'Ort'], ['auftraggeber', 'Auftraggeber'], ['notizen', 'Notizen (LV-Nr., Datum)'],
                ].map(([feld, label]) => (
                  <div key={feld} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
                    <input
                      style={inputStyle}
                      value={neuFelder[feld]}
                      onChange={(e) => setNeuFelder((prev) => ({ ...prev, [feld]: e.target.value }))}
                    />
                  </div>
                ))}
                {aehnliche.length > 0 && (
                  <div style={{
                    gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-3)',
                    background: 'var(--bg-elevated)', padding: '8px 12px', borderRadius: 'var(--radius)',
                  }}>
                    💡 Ähnliche vorhandene Baustelle{aehnliche.length > 1 ? 'n' : ''}:{' '}
                    {aehnliche.map((b) => b.name).join(', ')} – ggf. oben „Vorhandene Baustelle" wählen,
                    um Dubletten zu vermeiden.
                  </div>
                )}
              </div>
            ) : (
              <select
                style={{ ...inputStyle, maxWidth: 380 }}
                value={zielBaustelleId}
                onChange={(e) => setZielBaustelleId(e.target.value)}
              >
                <option value="">— Baustelle wählen —</option>
                {baustellen.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>

          {/* Schritt 3: Positions-Vorschau */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-head">
              <div className="card-title">
                3 · Vorschau · {rows.length} erkannte Position{rows.length === 1 ? '' : 'en'}
                <span style={{ marginLeft: 8, color: 'var(--text-3)', fontWeight: 400, fontSize: 12 }}>
                  {/* E3.2: quellenabhaengig - PDF liefert statistik.seiten,
                      Tabelle statistik.zeilen (+ sheet). Nie "undefined Seiten". */}
                  aus „{ergebnis.dateiname}"
                  {ergebnis.statistik.seiten != null
                    ? ` · ${ergebnis.statistik.seiten} Seite${ergebnis.statistik.seiten === 1 ? '' : 'n'}`
                    : ergebnis.statistik.zeilen != null
                      ? ` · ${ergebnis.statistik.zeilen} Zeile${ergebnis.statistik.zeilen === 1 ? '' : 'n'}${ergebnis.statistik.sheet ? ` · Blatt „${ergebnis.statistik.sheet}"` : ''}`
                      : ''}
                </span>
              </div>
            </div>
            {/* E3.2: Transparenz-Hinweise aus dem Tabellen-Parser. */}
            {(ergebnis.statistik.hinweis || ergebnis.statistik.weitereSheets > 0) && (
              <div style={{
                background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)',
                borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 12,
                color: 'var(--text-2)', marginBottom: 10, lineHeight: 1.5,
              }}>
                {ergebnis.statistik.hinweis && <div>⚠ {ergebnis.statistik.hinweis}</div>}
                {ergebnis.statistik.weitereSheets > 0 && (
                  <div>⚠ Datei enthält {ergebnis.statistik.weitereSheets + 1} Blätter – nur das erste („{ergebnis.statistik.sheet}") wurde verarbeitet.</div>
                )}
              </div>
            )}
            <div className="table-wrap table-mobile-stack">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Pos.-Nr.</th>
                    <th>Kurztext</th>
                    <th>Kategorie</th>
                    <th>Menge</th>
                    <th>Einheit</th>
                    <th>EP</th>
                    <th>GP</th>
                    <th style={{ width: 60 }}>Info</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const warnungen = zeileWarnungen(r, rows, bestehendePosNr);
                    const valide = zeileValide(r);
                    return (
                      <FragmentRow
                        key={idx}
                        idx={idx}
                        r={r}
                        warnungen={warnungen}
                        valide={valide}
                        offen={langtextOffen.has(idx)}
                        onToggleLangtext={() => toggleLangtext(idx)}
                        onChange={(patch) => setRow(idx, patch)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Uebersprungene Bloecke (einklappbar, reine Anzeige) */}
          {ergebnis.uebersprungen.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <button
                type="button"
                onClick={() => setZeigeUebersprungen((v) => !v)}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span>{zeigeUebersprungen ? '▾' : '▸'}</span>
                Übersprungene Blöcke · {ergebnis.uebersprungen.length}
                <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-3)' }}>
                  (Vorbemerkungen, Summen – werden nicht importiert)
                </span>
              </button>
              {zeigeUebersprungen && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {ergebnis.uebersprungen.map((u, i) => (
                    <div key={i} style={{
                      fontSize: 12, color: 'var(--text-3)', background: 'var(--bg-elevated)',
                      padding: '6px 10px', borderRadius: 'var(--radius)', lineHeight: 1.5,
                    }}>
                      <span style={{
                        display: 'inline-block', minWidth: 110, fontWeight: 600,
                        color: u.typ === 'unvollstaendig' ? 'var(--amber, #d49a3a)' : 'var(--text-3)',
                      }}>
                        {u.typ === 'vorbemerkung' ? 'Vorbemerkung' : u.typ === 'summe' ? 'Summenzeile' : u.typ === 'unvollstaendig' ? 'Unvollständig' : 'Unbekannt'}
                      </span>
                      {' '}S. {u.seite} · {u.zeilen} Zeile{u.zeilen === 1 ? '' : 'n'} · {u.auszug}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Schritt 4: Zusammenfassung + Bestaetigung */}
          <div className="card card-accent" style={{ marginBottom: 24 }}>
            <div className="card-head"><div className="card-title">4 · Import bestätigen</div></div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 12 }}>
              <strong>{ausgewaehlt.length}</strong> von {rows.length} Positionen ausgewählt ·
              GP-Summe (Auswahl): <strong>{fmtEur(summeGpAusgewaehlt)}</strong> ·
              Warnungen in Auswahl: <strong>{warnungenGesamt}</strong>
              <br />
              Ziel: {zielModus === 'neu'
                ? (neuFelder.name.trim() ? <>neue Baustelle <strong>„{neuFelder.name.trim()}"</strong></> : <em>neue Baustelle – Name fehlt noch</em>)
                : (zielBaustelleId
                    ? <>vorhandene Baustelle <strong>„{(baustellen.find((b) => String(b.id) === String(zielBaustelleId)) || {}).name}"</strong></>
                    : <em>bitte Baustelle wählen</em>)}
              <br />
              <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                Import als Hauptauftrag (nachtrag = nein), Quelle „lv", Datei „{ergebnis.dateiname}".
                Langtexte werden mit importiert (Kurztext geht in „Bezeichnung").
              </span>
            </div>
            {ausgewaehltInvalid.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--amber, #d49a3a)', marginBottom: 10 }}>
                ⚠ {ausgewaehltInvalid.length} ausgewählte Zeile{ausgewaehltInvalid.length === 1 ? ' ist' : 'n sind'} unvollständig –
                bitte Felder ergänzen oder abwählen.
              </div>
            )}
            {saveError && (
              <div style={{
                marginBottom: 10, padding: '10px 12px', borderRadius: 'var(--radius)',
                background: 'rgba(220,80,80,0.12)', border: '1px solid rgba(220,80,80,0.4)',
                color: 'var(--text-1)', fontSize: 13,
              }}>
                ⚠️ {saveError}
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary"
              disabled={!importMoeglich}
              onClick={importieren}
              style={{ opacity: importMoeglich ? 1 : 0.5 }}
            >
              {saving ? '⏳ Importiert…' : `✔ ${ausgewaehlt.length} Position${ausgewaehlt.length === 1 ? '' : 'en'} importieren`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Eine Vorschau-Zeile + optional aufgeklappter Langtext-Prüfkontext.
// Checkbox nur waehlbar, wenn die Zeile valide ist (Server-Regeln).
function FragmentRow({ idx, r, warnungen, valide, offen, onToggleLangtext, onChange }) {
  return (
    <>
      <tr style={{ opacity: r.ausgewaehlt ? 1 : 0.55 }}>
        <td data-label="Import">
          <input
            type="checkbox"
            checked={r.ausgewaehlt}
            disabled={!valide && !r.ausgewaehlt}
            onChange={(e) => onChange({ ausgewaehlt: e.target.checked })}
            title={!valide ? 'Zeile unvollständig – erst Felder ergänzen' : 'Zeile importieren'}
          />
        </td>
        <td data-label="Pos.-Nr." style={{ minWidth: 90 }}>
          <input style={{ ...cellInputStyle, fontFamily: 'var(--mono, monospace)' }} value={r.pos_nr || ''}
            onChange={(e) => onChange({ pos_nr: e.target.value })} />
        </td>
        <td data-label="Kurztext" className="td-title" style={{ minWidth: 220 }}>
          <input style={cellInputStyle} value={r.bezeichnung || ''}
            onChange={(e) => onChange({ bezeichnung: e.target.value })} />
        </td>
        <td data-label="Kategorie" style={{ minWidth: 140 }}>
          <input style={cellInputStyle} value={r.kategorie || ''}
            onChange={(e) => onChange({ kategorie: e.target.value })} />
        </td>
        <td data-label="Menge" style={{ minWidth: 90 }}>
          <input type="number" step="any" min="0" style={{ ...cellInputStyle, textAlign: 'right' }}
            value={r.sollmenge ?? ''}
            onChange={(e) => onChange({ sollmenge: e.target.value === '' ? '' : Number(e.target.value) })} />
        </td>
        <td data-label="Einheit" style={{ minWidth: 70 }}>
          <input style={{ ...cellInputStyle, textAlign: 'center' }} value={r.einheit || ''}
            onChange={(e) => onChange({ einheit: e.target.value })} />
        </td>
        <td data-label="EP" style={{ minWidth: 90 }}>
          <input type="number" step="any" min="0" style={{ ...cellInputStyle, textAlign: 'right' }}
            value={r.einheitspreis ?? ''}
            onChange={(e) => onChange({ einheitspreis: e.target.value === '' ? '' : Number(e.target.value) })} />
        </td>
        <td data-label="GP" style={{ minWidth: 100 }}>
          <input type="number" step="any" min="0" style={{ ...cellInputStyle, textAlign: 'right' }}
            value={r.gesamtpreis ?? ''}
            onChange={(e) => onChange({ gesamtpreis: e.target.value === '' ? '' : Number(e.target.value) })} />
        </td>
        <td data-label="Info">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {r.langtext && (
              <button type="button" onClick={onToggleLangtext} title="Langtext anzeigen (wird mit importiert)"
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-3)', cursor: 'pointer', fontSize: 11, padding: '2px 6px' }}>
                {offen ? '▾ Text' : '▸ Text'}
              </button>
            )}
            {warnungen.length > 0 && (
              <span title={warnungen.join('\n')} style={{ color: 'var(--amber, #d49a3a)', fontSize: 13, cursor: 'help' }}>
                ⚠ {warnungen.length}
              </span>
            )}
          </div>
        </td>
      </tr>
      {warnungen.length > 0 && r.ausgewaehlt && (
        <tr>
          <td></td>
          <td colSpan={8} data-label="Warnungen" style={{ paddingTop: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--amber, #d49a3a)', lineHeight: 1.5 }}>
              {warnungen.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          </td>
        </tr>
      )}
      {offen && r.langtext && (
        <tr>
          <td></td>
          <td colSpan={8} data-label="Langtext" style={{ paddingTop: 0 }}>
            <div style={{
              fontSize: 11, color: 'var(--text-3)', background: 'var(--bg-elevated)',
              padding: '8px 10px', borderRadius: 'var(--radius)', whiteSpace: 'pre-wrap', lineHeight: 1.5,
            }}>
              {r.langtext}
              <div style={{ marginTop: 6, fontStyle: 'italic' }}>
                Langtext wird mit gespeichert (Kurztext geht in „Bezeichnung").
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
